import React, { useState, useEffect, useCallback } from 'react';
import EmprestimosDoBanco from './EmprestimosDoBanco';
import { exemplosDeDivida } from '../utils/exemplosPessoais';
import {
  getDebts, deleteDebt, updateDebt, createDebt, askDebt, createGoal, getGoals,
  quitarDivida, desfazerQuitacao, ligarLancamentoDaDivida,
  Debt, DebtAnswer, ParsedDebt, Quitacao, PedidoDeQuitacao, DEBT_TYPE_LABEL,
} from '../services/api';
import { Transaction, TransactionType } from '../types/Transaction';
import { PaymentMethod } from '../types/PaymentMethod';
import { Investment } from '../types/Investment';
import { Goal } from '../types/Goal';
import QuitarDivida from './QuitarDivida';
import EditarDivida, { MudancasDaDivida } from './EditarDivida';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { brlOrMasked } from '../utils/format';
import AskField from './AskField';
import DebtProposal from './DebtProposal';
import DebtAnswerView from './DebtAnswer';
import Toast from './Toast';
import './Debts.css';
import { useTelaEstreita } from '../utils/telaEstreita';
import { useConfirmar } from '../contexts/Confirmacao';

/**
 * Dívidas — consulta, não lançamento.
 *
 * A seção existe por um motivo específico: um consignado é descontado do
 * salário na origem. O salário lançado já vem líquido, então a parcela **não
 * existe em Transações** e o app, sem esta tabela, não teria como saber que
 * R$ 1.950 saem todo mês nem que eles param em determinado mês.
 *
 * O campo aqui é SÓ de dívida, e é próprio da seção — não o de lançamento.
 * Dívida não cai em transação, em saldo nem em gasto do mês, então dividir a
 * mesma caixa com lançamento obrigava a saber de antemão em qual das duas
 * ela estava escrevendo. Aqui não existe essa dúvida: tudo que entra é dívida.
 *
 * E ele faz mais do que registrar. Pergunta de onde sai a parcela, se precisa
 * lançar, quanto sobra para adiantar, e propõe uma reserva para amortizar —
 * porque é sobre isso que se conversa quando o assunto é dívida.
 *
 * Todo número das respostas vem calculado do servidor. Nenhum passa pelo
 * modelo: numa conversa sobre quitar dívida, um número inventado com cara de
 * confiável é o erro mais caro que este app pode cometer.
 */

/** 'YYYY-MM-DD' de hoje, pelo relógio dela. */
const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Resultado = { answer: DebtAnswer | null; proposal: ParsedDebt | null };

interface Props {
  hideValues?: boolean;
  onChange?: () => void;
  /* Quitação (16/09): as origens do dinheiro e a despesa da parcela. */
  transactions?: Transaction[];
  paymentMethods?: PaymentMethod[];
  investments?: Investment[];
  /** A quitação mexeu em lançamento, investimento ou reserva: o App recarrega. */
  onDinheiroMudou?: () => void;
  /** O Open Finance está ligado para esta conta: oferece buscar os empréstimos no banco. */
  bancoDisponivel?: boolean;
}

const diaLegivel = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const ORIGEM_PADRAO: Record<Quitacao['sourceType'], string> = {
  saldo: 'saldo', investment: 'investimento', goal: 'reserva',
};

const mesLegivel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[(m || 1) - 1]}/${y}`;
};

const Debts: React.FC<Props> = ({
  hideValues = false, onChange, transactions = [], paymentMethods = [], investments = [], onDinheiroMudou,
  bancoDisponivel = false,
}) => {
  const confirmar = useConfirmar();
  const estreita = useTelaEstreita();
  const [debts, setDebts] = useState<Debt[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [proposta, setProposta] = useState<ParsedDebt | null>(null);
  const [primeiraParcela, setPrimeiraParcela] = useState(hojeISO());
  // Cada proposta nova remonta o card: os campos guardam o texto digitado.
  const [propostaN, setPropostaN] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [criandoMeta, setCriandoMeta] = useState(false);
  const [feito, setFeito] = useState<string | null>(null);

  const fmt = (v: number) => brlOrMasked(v, hideValues);
  const [quitando, setQuitando] = useState<Debt | null>(null);
  const [editando, setEditando] = useState<Debt | null>(null);
  const [reservas, setReservas] = useState<Goal[]>([]);

  const carregar = useCallback(async () => {
    try {
      setDebts(await getDebts());
      setErro(null);
    } catch {
      setErro('Não consegui carregar as dívidas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const apagar = async (d: Debt) => {
    if (!await confirmar({
      titulo: 'Apagar esta dívida?',
      alvo: d.name,
      texto: 'Ela some das projeções. Nenhuma transação é afetada: parcela descontada em folha nunca virou lançamento.',
      acao: 'Apagar', perigo: true,
    })) return;
    await deleteDebt(d.id);
    await carregar();
    onChange?.();
  };

  /*
   * Quitação (16/09). As reservas não moram no App — Metas carrega as dela —,
   * então vêm na hora de abrir a janela, já filtradas para as que têm dinheiro.
   */
  const abrirQuitacao = async (d: Debt) => {
    try {
      const metas = await getGoals();
      setReservas(metas.filter(g => g.type === 'reserve' && g.savedAmount > 0));
    } catch {
      setReservas([]);
    }
    setQuitando(d);
  };

  const quitar = async (d: Debt, pedido: PedidoDeQuitacao) => {
    await quitarDivida(d.id, pedido);
    setFeito(`Quitação de ${d.name} registrada`);
    await carregar();
    onChange?.();
    onDinheiroMudou?.();
  };

  const desfazer = async (d: Debt, q: Quitacao) => {
    if (!await confirmar({
      titulo: 'Desfazer esta quitação?',
      alvo: `${d.name} · ${fmt(q.amount)} em ${diaLegivel(q.paidOn)}`,
      texto: 'A dívida volta ao que era antes, e o dinheiro volta para '
        + (q.sourceType === 'saldo' ? 'o saldo (a saída da quitação é apagada).' : `${q.sourceName ?? ORIGEM_PADRAO[q.sourceType]}.`),
      acao: 'Desfazer', perigo: true,
    })) return;
    try {
      await desfazerQuitacao(d.id, q.id);
      await carregar();
      onChange?.();
      onDinheiroMudou?.();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui desfazer a quitação.'));
    }
  };

  const ligarLancamento = async (d: Debt, transactionId: string) => {
    try {
      await ligarLancamentoDaDivida(d.id, transactionId || null);
      await carregar();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui ligar o lançamento.'));
    }
  };

  // A despesa da parcela só pode ser mensal ou parcelada: é o que a quitação sabe ajustar.
  const despesasDeParcela = transactions
    .filter(t => t.type === TransactionType.EXPENSE && (t.isMonthly || t.hasInstallments) && !t.debitDirection)
    .sort((a, b) => a.description.localeCompare(b.description, 'pt-BR'));

  // O erro fica com a janela (ela não fecha), então aqui não se captura.
  const editar = async (d: Debt, mudancas: MudancasDaDivida) => {
    await updateDebt(d.id, mudancas);
    setFeito(`${mudancas.name ?? d.name} atualizada`);
    await carregar();
    onChange?.();
  };

  const alternarFolha = async (d: Debt) => {
    await updateDebt(d.id, { deductedFromSalary: !d.deductedFromSalary });
    await carregar();
    onChange?.();
  };

  // Só o que ainda pesa. Dívida quitada continua guardada — o histórico é dela —
  // mas não ocupa o topo da soma.
  const ativas = debts.filter(d => d.remainingInstallments > 0);
  const quitadas = debts.filter(d => d.remainingInstallments === 0);
  const totalMensal = ativas.reduce((s, d) => s + d.installmentValue, 0);
  const totalFalta = ativas.reduce((s, d) => s + d.remainingTotal, 0);
  // A primeira parcela a acabar é a primeira folga real no orçamento.
  const proximaFolga = ativas.length
    ? ativas.reduce((a, b) => (a.lastInstallmentMonth <= b.lastInstallmentMonth ? a : b))
    : null;

  const perguntar = async (texto: string): Promise<Resultado> => {
    const r = await askDebt(texto);
    // 200 sem conteúdo utilizável: o AskField transforma o throw em mensagem.
    if (!r.answer && !r.proposal) throw new Error(r.problem ?? 'Não entendi.');
    setFeito(null);
    if (r.proposal) { setPrimeiraParcela(hojeISO()); setPropostaN(n => n + 1); }
    setProposta(r.proposal);
    return { answer: r.answer, proposal: r.proposal };
  };

  const gravarProposta = async () => {
    if (!proposta || salvando) return;
    setSalvando(true);
    try {
      await createDebt({
        name: proposta.name.trim(),
        type: proposta.type,
        installments: proposta.installments!,
        installmentValue: proposta.installmentValue!,
        firstInstallmentDate: primeiraParcela,
        deductedFromSalary: proposta.deductedFromSalary!,
        monthlyRate: proposta.monthlyRate,
      });
      setFeito(`${proposta.name.trim()} registrada`);
      setProposta(null);
      await carregar();
      onChange?.();
    } catch {
      setErro('Não consegui salvar a dívida. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const criarMeta = async (nome: string, targetAmount: number, savedAmount: number) => {
    setCriandoMeta(true);
    try {
      // Uma reserva comum, do tipo que já existe em Metas — o dinheiro fica
      // dela e ela decide se usa para amortizar. Nada aqui manda para o banco.
      await createGoal({ type: 'reserve', name: nome, targetAmount, savedAmount, color: '#f59e0b' });
      setFeito(`Reserva "${nome}" criada em Metas`);
      onChange?.();
    } catch {
      setErro('Não consegui criar a reserva.');
    } finally {
      setCriandoMeta(false);
    }
  };

  /**
   * Uma linha da lista. Existe como função porque a lista de quitadas usa a
   * MESMA — duas cópias divergiriam, e a primeira a divergir seria a que
   * ninguém olha.
   */
  const linha = (d: Debt) => {
    const pago = d.installments > 0 ? (d.paidInstallments / d.installments) * 100 : 0;
    const quitada = d.remainingInstallments === 0;
    return (
      <div key={d.id} className={`dbt-item${quitada ? ' quitada' : ''}`}>
        <div className="dbt-item-topo">
          <div className="dbt-item-nome">
            <strong>{d.name}</strong>
            <span className="dbt-tag">{DEBT_TYPE_LABEL[d.type]}</span>
          </div>
          {/* O número da linha é o que SAI POR MÊS — é ele que soma no
              compromisso mensal e é ele que ela sente. O total de parcelas
              desceu para a linha de baixo, onde "12 de 36" já diz quantas. */}
          <span className="dbt-item-parcela">
            {fmt(d.installmentValue)}<em>/mês</em>
          </span>
        </div>

        <div className="dbt-barra" role="img"
             aria-label={`${d.paidInstallments} de ${d.installments} parcelas pagas`}>
          <div className="dbt-barra-preenchida" style={{ width: `${Math.min(pago, 100)}%` }} />
        </div>

        <div className="dbt-item-linha">
          <span>{d.paidInstallments} de {d.installments} pagas</span>
          <span>
            {quitada
              ? `terminou em ${mesLegivel(d.lastInstallmentMonth)}`
              : `falta ${fmt(d.remainingTotal)} · até ${mesLegivel(d.lastInstallmentMonth)}`}
          </span>
        </div>

        {/* A frase vem pronta do servidor (`debt.frasePorDia`): conversão, nunca
            instrução. Aqui só se exibe. */}
        {!quitada && d.porDiaTexto && <p className="dbt-por-dia">{d.porDiaTexto}</p>}

        {/* O lançamento da parcela (16/09): só em dívida paga pela conta. Ligado,
            a quitação ajusta ele junto. */}
        {!quitada && !d.deductedFromSalary && (
          <div className="dbt-lancamento">
            <label htmlFor={`dbt-lanc-${d.id}`}>Lançamento da parcela</label>
            <select
              id={`dbt-lanc-${d.id}`}
              value={d.transactionId ?? ''}
              onChange={e => ligarLancamento(d, e.target.value)}
            >
              <option value="">nenhum</option>
              {despesasDeParcela.map(t => (
                <option key={t.id} value={t.id}>
                  {t.description} · {fmt(t.value)}{t.isMonthly ? '/mês' : ` em ${t.installments}x`}
                </option>
              ))}
            </select>
          </div>
        )}

        {(d.quitacoes?.length ?? 0) > 0 && (
          <details className="dbt-historico">
            <summary>
              {d.quitacoes!.length} {d.quitacoes!.length === 1 ? 'quitação' : 'quitações'}
              {(d.economiaTotal ?? 0) > 0 && <> · economizou <strong>{fmt(d.economiaTotal!)}</strong></>}
            </summary>
            <ul>
              {d.quitacoes!.map((q, i) => {
                const antes = Math.round(q.remainingBefore / q.installmentValueBefore);
                const depois = q.installmentValueAfter > 0 ? Math.round(q.remainingAfter / q.installmentValueAfter) : 0;
                return (
                  <li key={q.id}>
                    <div className="dbt-historico-linha">
                      <span>{diaLegivel(q.paidOn)} · pagou <strong>{fmt(q.amount)}</strong> ({q.sourceName ?? ORIGEM_PADRAO[q.sourceType]})</span>
                      {i === 0 && (
                        <button className="dbt-desfazer" onClick={() => desfazer(d, q)}>desfazer</button>
                      )}
                    </div>
                    <div className="dbt-historico-detalhe">
                      {q.mode === 'parcelas'
                        ? (depois === 0 ? `quitou as ${antes} parcelas` : `de ${antes} para ${depois} parcelas`)
                        : `parcela de ${fmt(q.installmentValueBefore)} para ${fmt(q.installmentValueAfter)}`}
                      {' · '}
                      {q.savings >= 0 ? `economizou ${fmt(q.savings)}` : `custou ${fmt(-q.savings)} a mais`}
                    </div>
                  </li>
                );
              })}
            </ul>
          </details>
        )}

        <div className="dbt-item-acoes">
          {/* Onde a parcela sai muda se ela deve lançá-la ou não — e o app só
              pode saber pela resposta dela. Numa dívida que acabou a pergunta
              não tem mais efeito, então some. */}
          {!quitada ? (
            <button className="dbt-toggle" onClick={() => alternarFolha(d)}>
              {/* Curto, mas ainda dizendo a CONSEQUÊNCIA e não "sim/não": é a
                  diferença entre a parcela existir em Transações ou não. A
                  versão longa quebrava em duas linhas no celular e voltava a
                  ser o elemento mais pesado da linha. */}
              {d.deductedFromSalary
                ? 'sai do salário, não lanço'
                : 'pago por fora, eu lanço'}
            </button>
          ) : <span />}
          <span className="dbt-acoes-direita">
            <button className="dbt-editar" onClick={() => setEditando(d)}>Editar</button>
            {!quitada && <button className="dbt-quitar" onClick={() => abrirQuitacao(d)}>Quitar</button>}
            <button className="dbt-apagar" onClick={() => apagar(d)}>apagar</button>
          </span>
        </div>
      </div>
    );
  };

  if (carregando) return <div className="dbt-container"><h2>Dívidas</h2><p className="dbt-vazio">Carregando…</p></div>;

  return (
    <div className="dbt-container">
      <div className="dbt-header">
        <h2>Dívidas</h2>
        {ativas.length > 0 && <span className="dbt-badge">{ativas.length}</span>}
      </div>

      {erro && <p className="dbt-erro">{erro}</p>}

      {/* Confirmação some sozinha: registrar uma dívida é um evento, e evento
          que fica na tela vira parte do card. Vai por portal, fora do card. */}
      <Toast message={feito} onDismiss={() => setFeito(null)} />

      {quitando && (
        <QuitarDivida
          debt={quitando}
          metodos={paymentMethods}
          investimentos={investments.filter(i => i.amount > 0)}
          reservas={reservas}
          fmt={fmt}
          onConfirmar={pedido => quitar(quitando, pedido)}
          onFechar={() => setQuitando(null)}
        />
      )}

      {editando && (
        <EditarDivida
          debt={editando}
          onSalvar={mudancas => editar(editando, mudancas)}
          onFechar={() => setEditando(null)}
        />
      )}

      <AskField<Resultado>
        placeholder={
          /* Medido num Android de 360px: o longo pede 297px numa caixa de 226,
             este pede 208. Ver `utils/telaEstreita.ts`. */
          estreita ? 'registre uma dívida, ou pergunte' : 'registre uma dívida ou pergunte sobre as suas'
        }
        // Com o nome de uma dívida dela, quando há: `utils/exemplosPessoais.ts`.
        exemplos={exemplosDeDivida(debts.map(d => d.name))}
        ariaLabel="Campo de dívidas"
        onAsk={perguntar}
      >
        {(r, limpar) => (
          r.proposal && proposta ? (
            <DebtProposal
              key={propostaN}
              debt={proposta}
              primeiraParcela={primeiraParcela}
              onPrimeiraParcela={setPrimeiraParcela}
              onChange={setProposta}
              onSave={async () => { await gravarProposta(); limpar(); }}
              onCancel={() => { setProposta(null); limpar(); }}
              salvando={salvando}
            />
          ) : r.answer ? (
            <DebtAnswerView
              answer={r.answer}
              hideValues={hideValues}
              onCreateGoal={criarMeta}
              criandoMeta={criandoMeta}
            />
          ) : null
        )}
      </AskField>

      {bancoDisponivel && (
        <EmprestimosDoBanco hideValues={hideValues} onCadastrou={() => { void carregar(); onChange?.(); }} />
      )}

      {debts.length === 0 ? (
        // O mesmo desenho de Investimentos, Metas e Lembretes: uma prévia
        // fantasma do que a seção vira, e a chamada embaixo. Ver a parte
        // compartilhada em App.css (`empty-preview-*`, `empty-cta-*`).
        <div className="empty-preview-wrap">
          <span className="empty-preview-badge">Exemplo</span>
          {/*
            O exemplo traz também o resumo, com números que batem com as duas
            dívidas de baixo: 1.950 + 890 por mês, e (24 × 1.950) + (18 × 890)
            de falta. A primeira a acabar é o financiamento, em fev/2027.
          */}
          <div className="empty-preview-ghost" aria-hidden="true">
            <div className="dbt-resumo">
              <div className="dbt-resumo-caixa principal">
                <span className="dbt-resumo-label">Sai por mês</span>
                <strong className="dbt-resumo-valor">{fmt(2840)}</strong>
              </div>
              <div className="dbt-resumo-caixa">
                <span className="dbt-resumo-label">Falta, com juros</span>
                <strong className="dbt-resumo-valor">{fmt(62820)}</strong>
              </div>
              <div className="dbt-resumo-caixa">
                <span className="dbt-resumo-label">Primeira folga</span>
                <strong className="dbt-resumo-valor">fev/2027</strong>
              </div>
            </div>
            <ul className="dbt-resumo-apoio">
              <li><strong>Falta, com juros:</strong> é a soma das parcelas. Quitar antes custa menos.</li>
              <li><strong>Primeira folga:</strong> em fev/2027 acaba Financiamento carro e sobram {fmt(890)} por mês.</li>
            </ul>
          <div className="dbt-lista">
            {[
              { nome: 'Consignado Banco X', tipo: 'Consignado', parcelas: 36, valor: 1950, pagas: 12, ate: 'ago/2028' },
              { nome: 'Financiamento carro', tipo: 'Financiamento', parcelas: 48, valor: 890, pagas: 30, ate: 'fev/2027' },
            ].map((d, i) => (
              <div key={i} className="dbt-item">
                <div className="dbt-item-topo">
                  <div className="dbt-item-nome">
                    <strong>{d.nome}</strong>
                    <span className="dbt-tag">{d.tipo}</span>
                  </div>
                  <span className="dbt-item-parcela">{d.parcelas}× {fmt(d.valor)}</span>
                </div>
                <div className="dbt-barra">
                  <div className="dbt-barra-preenchida" style={{ width: `${(d.pagas / d.parcelas) * 100}%` }} />
                </div>
                <div className="dbt-item-linha">
                  <span>{d.pagas} de {d.parcelas} pagas</span>
                  <span>falta {fmt((d.parcelas - d.pagas) * d.valor)} · até {d.ate}</span>
                </div>
              </div>
            ))}
          </div>
          </div>
          <div className="empty-cta">
            <p className="empty-cta-title">Nenhuma dívida registrada</p>
            <p className="empty-cta-sub">
              Escreva no campo acima, por exemplo <em>“fiz um consignado de 53 mil em 36x de 1.950”</em>,
              e o app passa a saber quando a parcela acaba. Dívida não entra em gastos nem
              mexe no seu saldo.
            </p>
          </div>
        </div>
      ) : (
        <>
          {ativas.length > 0 && (
            <>
              {/*
                Caixas pequenas, no estilo dos números da conta da casa e do
                resumo de investimentos. Antes era um bloco âmbar de largura
                cheia com o número em tamanho de manchete e dois parágrafos:
                tomava mais tela do que a lista de dívidas (pedido dela, 06/10).

                O que se mantém da versão anterior, e não é detalhe:

                - UMA caixa em destaque, a do que sai por mês. As outras são
                  neutras. Três caixas de peso igual fariam "falta pagar" ser
                  lida como saldo devedor, e ela é a menos acionável das três.
                - A ressalva continua colada no número que ela corrige: o
                  rótulo diz "com juros", e a nota de baixo diz que quitar
                  antes custa menos. O número é de dezenas de milhares.
              */}
              <div className="dbt-resumo" role="group" aria-label="Resumo das dívidas">
                <div className="dbt-resumo-caixa principal">
                  <span className="dbt-resumo-label">Sai por mês</span>
                  <strong className="dbt-resumo-valor">{fmt(totalMensal)}</strong>
                </div>
                <div className="dbt-resumo-caixa">
                  <span className="dbt-resumo-label">Falta, com juros</span>
                  <strong className="dbt-resumo-valor">{fmt(totalFalta)}</strong>
                </div>
                {proximaFolga && (
                  <div className="dbt-resumo-caixa">
                    <span className="dbt-resumo-label">Primeira folga</span>
                    <strong className="dbt-resumo-valor">{mesLegivel(proximaFolga.lastInstallmentMonth)}</strong>
                  </div>
                )}
              </div>
              {/* Uma ideia por linha, cada uma começando pelo nome da caixa que
                  explica. Numa frase só, as duas se emendavam e era preciso
                  reler para saber onde acabava uma e começava a outra. */}
              <ul className="dbt-resumo-apoio">
                <li>
                  <strong>Falta, com juros:</strong> é a soma das parcelas. Quitar antes custa menos.
                </li>
                {proximaFolga && (
                  <li>
                    <strong>Primeira folga:</strong> em {mesLegivel(proximaFolga.lastInstallmentMonth)} acaba
                    {' '}{proximaFolga.name} e sobram {fmt(proximaFolga.installmentValue)} por mês.
                  </li>
                )}
              </ul>
            </>
          )}

          <div className="dbt-lista">
            {ativas.map(linha)}
          </div>

          {/* Quitada é histórico: ocupava linha inteira, com um ajuste que já
              não muda nada, para dizer "acabou". Recolhida, o card mostra o
              que ainda pesa e ela abre o resto quando quiser. `details` porque
              o navegador já sabe fazer isto — teclado e leitor de tela junto. */}
          {quitadas.length > 0 && (
            <details className="dbt-quitadas">
              <summary>
                {quitadas.length} {quitadas.length === 1 ? 'dívida quitada' : 'dívidas quitadas'}
              </summary>
              <div className="dbt-lista">{quitadas.map(linha)}</div>
            </details>
          )}
        </>
      )}
    </div>
  );
};

export default Debts;
